import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, ChevronDown, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { WizardStepper } from '@/components/custom/WizardStepper';
import { BUILD_SUBCATEGORIES } from '@uniticket/shared';
import { createTicketWithExtras, searchBuildAreas } from '@/hooks/useTickets';
import { AttachmentPicker } from '@/components/tickets/AttachmentPicker';
import { AreaCombobox } from '@/components/tickets/AreaCombobox';
import { SimilarTicketsBanner, SimilarTicketsConfirmDialog } from '@/components/tickets/SimilarTicketsBanner';
import type { AreaOption, SimilarTicketItem } from '@uniticket/shared';

/**
 * Create wizard for BUILD tickets.
 *
 * Three steps rather than SupportWizard's two: the category tile grid is its own step so that
 * further BUILD categories (deployment areas, etc.) drop in as data — appending to
 * BUILD_SUBCATEGORIES plus one i18n key — with no layout rework. Today it renders a single tile.
 */
export function BuildWizard() {
  const navigate = useNavigate();
  const { t } = useTranslation(['tickets', 'common']);
  const [step, setStep] = useState(0);

  const [subcategory, setSubcategory] = useState('');
  const [area, setArea] = useState<AreaOption | null>(null);
  const [areaOptions, setAreaOptions] = useState<AreaOption[]>([]);
  const [areaLoading, setAreaLoading] = useState(false);
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('NORMAL');
  /** Optional specific addresses inside the area; editable again on the ticket afterwards. */
  const [jvids, setJvids] = useState<number[]>([]);
  const [jvidInput, setJvidInput] = useState('');

  // Customer contact
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactOpen, setContactOpen] = useState(false);

  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [pastedEmail, setPastedEmail] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [similarResults, setSimilarResults] = useState<SimilarTicketItem[]>([]);
  const [showSimilarConfirm, setShowSimilarConfirm] = useState(false);

  /**
   * Latest requested search term. Captured before each fetch and re-checked after the await, so a
   * slow response for "GF1" can never overwrite the results already shown for "GF12".
   */
  const searchSeq = useRef('');
  /** Which subcategory's area list has been pre-loaded, so returning to step 1 doesn't refetch. */
  const loadedSubcategory = useRef('');

  // Pre-load the subcategory's areas when the details step opens. public.area is small (260 rows
  // in total), so the whole prefix is browsable without typing.
  useEffect(() => {
    if (step !== 1 || !subcategory) return;
    if (loadedSubcategory.current === subcategory) return;
    loadedSubcategory.current = subcategory;
    void runAreaSearch('', subcategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, subcategory]);

  async function runAreaSearch(term: string, forSubcategory: string) {
    searchSeq.current = term;
    setAreaLoading(true);
    try {
      const res = await searchBuildAreas(forSubcategory, term);
      // Drop out-of-order responses and results for a subcategory the user has since left.
      if (searchSeq.current !== term || loadedSubcategory.current !== forSubcategory) return;
      setAreaOptions(res.data);
    } catch {
      if (searchSeq.current !== term) return;
      setAreaOptions([]);
    } finally {
      if (searchSeq.current === term) setAreaLoading(false);
    }
  }

  function handleSelectSubcategory(sub: string) {
    const next = sub === subcategory ? '' : sub;
    setSubcategory(next);
    // Reset everything the previous category populated — the area list is category-specific.
    setArea(null);
    setAreaOptions([]);
    loadedSubcategory.current = '';
    searchSeq.current = '';
  }

  function addJvid() {
    const jvid = parseInt(jvidInput, 10);
    if (isNaN(jvid) || jvidInput.length !== 8) return;
    setJvids((prev) => (prev.includes(jvid) ? prev : [...prev, jvid]));
    setJvidInput('');
  }

  function handleSubmitClick() {
    if (!subcategory) { toast.error(t('tickets:buildWizard.categoryRequired')); return; }
    if (!area) { toast.error(t('tickets:buildWizard.areaRequired')); return; }
    if (!subject.trim()) { toast.error(t('common:validation.subjectRequired')); return; }
    if (similarResults.length > 0 && !showSimilarConfirm) {
      setShowSimilarConfirm(true);
      return;
    }
    void doSubmit();
  }

  async function doSubmit() {
    setShowSimilarConfirm(false);
    setSubmitting(true);
    try {
      const result = await createTicketWithExtras({
        ticketType: 'BUILD',
        subject: subject.trim(),
        description: description.trim() || undefined,
        priority,
        buildSubcategory: subcategory,
        // Snapshot the DMP row verbatim. areaReleased is passed through as-is, including null.
        areaId: area!.areaId,
        areaName: area!.areaName,
        areaProject: area!.areaProject ?? undefined,
        areaReleased: area!.areaReleased,
        areaReleasedDate: area!.areaReleasedDate ?? undefined,
        jvids: jvids.length ? jvids : undefined,
        contactFirstName: contactFirstName.trim() || undefined,
        contactLastName: contactLastName.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
      }, attachedFiles, pastedEmail);
      toast.success(t('tickets:create.created', { number: result.ticket.ticketNumber }));
      if (result.uploadErrors.length > 0) {
        toast.warning(t('tickets:customerEmail.someUploadsFailed', {
          count: result.uploadErrors.length,
          names: result.uploadErrors.map((e) => e.fileName).join(', '),
        }));
      }
      if (result.textAttachmentFailed) {
        toast.warning(t('tickets:customerEmail.textAttachmentFailed'));
      }
      navigate(`/tickets/${result.ticket.id}`);
    } catch (err: any) {
      toast.error(err?.message || t('tickets:create.createError'));
    } finally {
      setSubmitting(false);
    }
  }

  const canGoNext = step === 0 ? !!subcategory : !!area && !!subject.trim();

  return (
    <div className="max-w-4xl mx-auto">
      <WizardStepper
        steps={[
          t('tickets:buildWizard.steps.category'),
          t('tickets:create.steps.details'),
          t('tickets:create.steps.review'),
        ]}
        currentStep={step}
      />

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 mt-8">
        {/* Step 0: Category */}
        {step === 0 && (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">{t('tickets:buildWizard.categoryTitle')}</h2>
            <p className="text-sm text-slate-500 mb-6">{t('tickets:buildWizard.categoryHint')}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {BUILD_SUBCATEGORIES.map((sub) => (
                <button
                  key={sub}
                  onClick={() => handleSelectSubcategory(sub)}
                  className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                    subcategory === sub
                      ? 'border-brand-rose bg-brand-rose/5 ring-2 ring-brand-rose/20'
                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  <p className="text-sm font-semibold text-slate-900">
                    {t(`common:buildSubcategory.${sub}`)}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Details */}
        {step === 1 && (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">{t('tickets:buildWizard.detailsTitle')}</h2>
            <p className="text-sm text-slate-500 mb-6">{t('tickets:buildWizard.detailsHint')}</p>

            <div className="space-y-4 max-w-lg">
              <AreaCombobox
                label={t('tickets:buildWizard.area')}
                value={area}
                onChange={setArea}
                options={areaOptions}
                loading={areaLoading}
                onSearch={(q) => void runAreaSearch(q, subcategory)}
                placeholder={t('tickets:buildWizard.areaPlaceholder')}
                hint={t('tickets:buildWizard.areaHint')}
              />

              {area && <AreaSummary area={area} />}

              {/* Optional: specific addresses inside the area. Same list is editable on the
                  ticket afterwards, where each entry also opens its full DMP data. */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('tickets:attachedJvids.title')}
                </label>
                <p className="text-xs text-slate-400 mb-2">{t('tickets:buildWizard.jvidsHint')}</p>
                {jvids.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {jvids.map((j) => (
                      <span key={j} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-mono text-slate-700">
                        {j}
                        <button
                          type="button"
                          onClick={() => setJvids((prev) => prev.filter((x) => x !== j))}
                          className="text-slate-400 hover:text-red-600"
                          aria-label={t('tickets:attachedJvids.remove')}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={jvidInput}
                    onChange={(e) => setJvidInput(e.target.value.replace(/\D/g, '').slice(0, 8))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addJvid(); } }}
                    placeholder={t('tickets:attachedJvids.placeholder')}
                    className="flex-1 px-3 py-2 text-sm font-mono border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                  />
                  <button
                    type="button"
                    onClick={addJvid}
                    disabled={jvidInput.length !== 8}
                    className="px-3 py-2 text-sm font-medium rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('tickets:attachedJvids.add')}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('common:fields.subject')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t('tickets:buildWizard.subjectPlaceholder')}
                  maxLength={200}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                />
                <p className="text-xs text-slate-400 mt-1 text-right">{subject.length}/200</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('common:fields.description')}
                </label>
                <textarea
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('tickets:buildWizard.descriptionPlaceholder')}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">{t('common:fields.priority')}</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40"
                >
                  <option value="LOW">{t('common:priority.LOW')}</option>
                  <option value="NORMAL">{t('common:priority.NORMAL')}</option>
                  <option value="HIGH">{t('common:priority.HIGH')}</option>
                  <option value="URGENT">{t('common:priority.URGENT')}</option>
                </select>
              </div>

              <SimilarTicketsBanner
                subject={subject}
                description={description}
                areaId={area?.areaId}
                ticketType="BUILD"
                onResultsChange={setSimilarResults}
              />
            </div>

            {/* Customer email capture — paste + file attachments */}
            <div className="mt-6 max-w-lg space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  {t('tickets:customerEmail.label')}
                </label>
                <p className="text-xs text-slate-400 mb-2">{t('tickets:customerEmail.hint')}</p>
                <textarea
                  rows={6}
                  value={pastedEmail}
                  onChange={(e) => setPastedEmail(e.target.value)}
                  placeholder={t('tickets:customerEmail.placeholder')}
                  className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40 font-mono resize-y"
                />
              </div>
              <AttachmentPicker files={attachedFiles} onChange={setAttachedFiles} disabled={submitting} />
            </div>

            {/* Customer Contact — collapsible */}
            <div className="mt-6 border border-slate-200 rounded-xl overflow-hidden max-w-lg">
              <button
                onClick={() => setContactOpen(!contactOpen)}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
              >
                <span className="text-sm font-semibold text-slate-700">{t('tickets:supportWizard.customerContact')}</span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${contactOpen ? 'rotate-180' : ''}`} />
              </button>
              {contactOpen && (
                <div className="px-4 py-4">
                  <p className="text-xs text-slate-500 mb-3">{t('tickets:supportWizard.customerContactHint')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t('common:fields.contactFirstName')}</label>
                      <input type="text" value={contactFirstName} onChange={(e) => setContactFirstName(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t('common:fields.contactLastName')}</label>
                      <input type="text" value={contactLastName} onChange={(e) => setContactLastName(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t('common:fields.contactPhone')}</label>
                      <input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">{t('common:fields.contactEmail')}</label>
                      <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-rose/20 focus:border-brand-rose/40" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Review */}
        {step === 2 && (
          <div>
            <h2 className="text-xl font-bold text-slate-900 mb-1">{t('tickets:buildWizard.reviewTitle')}</h2>
            <p className="text-sm text-slate-500 mb-6">{t('tickets:buildWizard.reviewHint')}</p>

            <div className="space-y-4">
              <div className="border border-slate-200 rounded-lg p-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{t('common:ticketType.BUILD')}</h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  <ReviewField label={t('common:fields.type')} value={t('common:ticketType.BUILD')} />
                  <ReviewField
                    label={t('tickets:buildWizard.category')}
                    value={subcategory ? t(`common:buildSubcategory.${subcategory}`) : '—'}
                  />
                  <ReviewField label={t('common:fields.subject')} value={subject || '—'} />
                  <ReviewField label={t('common:fields.priority')} value={t(`common:priority.${priority}`)} />
                  <div className="col-span-2">
                    <ReviewField label={t('common:fields.description')} value={description || '—'} />
                  </div>
                </div>
              </div>

              {area && (
                <div className="border border-slate-200 rounded-lg p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{t('tickets:buildWizard.areaSection')}</h3>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                    <ReviewField label={t('common:fields.areaId')} value={area.areaId} />
                    <ReviewField label={t('common:fields.areaProject')} value={area.areaProject ?? '—'} />
                    <div className="col-span-2">
                      <ReviewField label={t('common:fields.areaName')} value={area.areaName} />
                    </div>
                    <ReviewField label={t('common:fields.areaReleased')} value={formatReleased(area.areaReleased, t)} />
                    <ReviewField label={t('common:fields.areaReleasedDate')} value={formatAreaDate(area.areaReleasedDate)} />
                  </div>
                </div>
              )}

              {(contactFirstName || contactLastName || contactPhone || contactEmail) && (
                <div className="border border-slate-200 rounded-lg p-4">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">{t('tickets:supportWizard.customerContact')}</h3>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                    <ReviewField label={t('common:fields.contactFirstName')} value={contactFirstName || '—'} />
                    <ReviewField label={t('common:fields.contactLastName')} value={contactLastName || '—'} />
                    <ReviewField label={t('common:fields.contactPhone')} value={contactPhone || '—'} />
                    <ReviewField label={t('common:fields.contactEmail')} value={contactEmail || '—'} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6">
        <div>
          {step === 0 ? (
            <Link to="/tickets/new" className="px-5 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              {t('common:actions.cancel')}
            </Link>
          ) : (
            <button onClick={() => setStep(step - 1)} className="px-5 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
              {t('common:actions.previous')}
            </button>
          )}
        </div>
        <div>
          {step < 2 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canGoNext}
              className="px-5 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common:actions.next')}
            </button>
          ) : (
            <button onClick={handleSubmitClick} disabled={submitting} className="px-5 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
              {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {submitting ? t('tickets:create.creating') : t('tickets:create.submitTicket')}
            </button>
          )}
        </div>
      </div>
      {showSimilarConfirm && similarResults.length > 0 && (
        <SimilarTicketsConfirmDialog
          results={similarResults}
          onConfirm={doSubmit}
          onCancel={() => setShowSimilarConfirm(false)}
        />
      )}
    </div>
  );
}

/** Read-only echo of the picked DMP row, so the user confirms the area before writing the ticket. */
function AreaSummary({ area }: { area: AreaOption }) {
  const { t } = useTranslation(['tickets', 'common']);
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="grid grid-cols-2 gap-x-6 gap-y-2">
        <ReviewField label={t('common:fields.areaId')} value={area.areaId} />
        <ReviewField label={t('common:fields.areaProject')} value={area.areaProject ?? '—'} />
        <div className="col-span-2">
          <ReviewField label={t('common:fields.areaName')} value={area.areaName} />
        </div>
        <ReviewField label={t('common:fields.areaReleased')} value={formatReleased(area.areaReleased, t)} />
        <ReviewField label={t('common:fields.areaReleasedDate')} value={formatAreaDate(area.areaReleasedDate)} />
      </div>
    </div>
  );
}

/**
 * Three-state, on purpose: DMP leaves area_released NULL on most rows and never sets it false, so
 * null means "DMP records nothing" — rendering that as "No" would assert something DMP has not.
 */
export function formatReleased(released: boolean | null, t: (k: string) => string): string {
  if (released === null || released === undefined) return '—';
  return released ? t('common:fields.areaReleasedYes') : t('common:fields.areaReleasedNo');
}

/**
 * DMP's naive wall clock ('YYYY-MM-DDTHH:mm:ss', no zone) shown as a plain date. Sliced rather
 * than parsed so no browser timezone is ever applied to a value that has none.
 */
export function formatAreaDate(value: string | null): string {
  if (!value) return '—';
  return value.slice(0, 10);
}

function ReviewField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{label}</p>
      <p className="text-sm text-slate-700 whitespace-pre-line">{value}</p>
    </div>
  );
}
